import { useCallback, useEffect, useState } from "react";

// Loads one API resource for a page: { data, meta, loading, error, reload }.
//
// `fetcher` returns a promise of the API envelope ({ data, meta }). The request
// re-runs whenever a value in `deps` changes, or when `reload()` is called. A
// stale response (the caller changed a filter, or the page unmounted) is
// dropped, never shown.
//
// Data from a previous request is kept while the SAME request reloads (so a
// refresh after saving does not blank the screen), but never across a change of
// `deps`: a different journey / page / filter must not show, or be acted on
// with, the previous one's data while its own is still loading.
//
// A failed request surfaces as `error` — there is deliberately NO fallback data:
// pages show the error and offer a retry rather than pretend the call worked.
//
// Optional `{ refreshMs }` keeps a read-only screen current (dashboard, live
// ranking, stats): every `refreshMs` while the tab is visible, and whenever the
// tab/window regains focus, the request re-runs SILENTLY — no loading state, the
// data on screen is only swapped once the new response arrives, and a failed
// background refresh keeps the last good data instead of replacing it with an
// error. Omitted (the default), nothing is polled.
export default function useTspApiResource(fetcher, deps = [], { refreshMs = 0 } = {}) {
  const depsKey = JSON.stringify(deps);
  const [state, setState] = useState({ key: depsKey, data: null, meta: null, loading: true, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...(previous.key === depsKey ? previous : { data: null, meta: null }), key: depsKey, loading: true, error: null }));

    const promise = fetcher?.();
    if (!promise || typeof promise.then !== "function") {
      setState({ key: depsKey, data: null, meta: null, loading: false, error: null });
      return;
    }

    promise
      .then((response) => {
        if (!cancelled) setState({ key: depsKey, data: response?.data ?? null, meta: response?.meta || null, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ key: depsKey, data: null, meta: null, loading: false, error });
      });

    return () => {
      cancelled = true;
    };
    // `fetcher` is intentionally omitted: callers pass inline closures and list
    // the values that should trigger a reload in `deps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, depsKey]);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);

  useEffect(() => {
    if (!refreshMs) return undefined;
    let cancelled = false;
    let inFlight = false;
    const refresh = () => {
      if (inFlight || (typeof document !== "undefined" && document.visibilityState === "hidden")) return;
      inFlight = true;
      const promise = fetcher?.();
      if (!promise || typeof promise.then !== "function") {
        inFlight = false;
        return;
      }
      promise
        .then((response) => {
          if (!cancelled) setState((previous) => (previous.key === depsKey && !previous.loading ? { key: depsKey, data: response?.data ?? null, meta: response?.meta || null, loading: false, error: null } : previous));
        })
        .catch(() => {}) // keep the last good data; the next tick tries again
        .finally(() => {
          inFlight = false;
        });
    };
    const timer = setInterval(refresh, refreshMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // Same contract as above: `fetcher` is an inline closure keyed by `deps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMs, depsKey]);

  // Between a change of deps and its effect running, `state` still belongs to the
  // old request: present that as "loading, no data" rather than the old data.
  const current = state.key === depsKey ? state : { data: null, meta: null, loading: true, error: null };
  return { data: current.data, meta: current.meta, loading: current.loading, error: current.error, reload };
}

// The server's own explanation when there is one (validation, conflict, ...),
// otherwise a plain fallback. Never the raw error object.
export function tspErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const status = error?.response?.status;
  if (status === 403) return "You do not have access to this.";
  // No answer from the server at all. axios (0.27) reports this as
  // code "ERR_NETWORK" with a `response` whose status is 0 — so "no response"
  // alone is not a reliable test.
  const unreachable = !error?.response || status === 0 || error.code === "ERR_NETWORK" || error.message === "Network Error";
  if (unreachable) return "Could not reach the server. Check your connection and try again.";
  return error.response.data?.message || fallback;
}
